// ============================================================
// STATISTICS & REPORTS PAGE
// ============================================================

import React, { useState, useEffect } from 'react';
import { BarChart, Bar, LineChart, Line, PieChart, Pie, Cell, ResponsiveContainer, XAxis, YAxis, CartesianGrid, Tooltip, Legend } from 'recharts';
import { Download } from 'lucide-react';
import { Button, Tabs } from '../components/ui';
import { getRevenueChartData, getOrderTrendData, getPickupStatusBreakdown, getCustomerGrowthData, getPeakHoursData } from '../services/mockData';
import { adminApi } from '../services/apiService';
import { formatCurrency } from '../utils/helpers';

const COLORS = ['#E85D04', '#F48C06', '#10B981', '#3B82F6', '#8B5CF6'];

const Statistics = () => {
  const [tab, setTab] = useState('revenue');
  const [revenueData, setRevenueData] = useState([]);
  const [orderData, setOrderData]     = useState([]);
  const [deliveryData, setDelivery]   = useState([]);
  const [peakData, setPeak]           = useState([]);
  const [customerData, setCustomer]   = useState([]);

  useEffect(() => {
    Promise.all([
      adminApi.getRevenueAnalytics('monthly', getRevenueChartData),
      adminApi.getOrderAnalytics('weekly', getOrderTrendData),
      adminApi.getPickupAnalytics(getPickupStatusBreakdown),
      adminApi.getPeakHours(getPeakHoursData),
    ]).then(([rev, ord, pickup, peak]) => {
      setRevenueData(Array.isArray(rev) ? rev : (rev?.items || getRevenueChartData()));
      setOrderData(Array.isArray(ord) ? ord : (ord?.items || getOrderTrendData()));
      // pickup from API has different shape than breakdown
      if (Array.isArray(pickup)) setDelivery(pickup);
      else if (pickup?.pickupRateByCity) setDelivery(getPickupStatusBreakdown()); // use mock breakdown
      else setDelivery(getPickupStatusBreakdown());
      setPeak(Array.isArray(peak) ? peak : getPeakHoursData());
      setCustomer(getCustomerGrowthData()); // no API endpoint yet
    }).catch(() => {
      setRevenueData(getRevenueChartData());
      setOrderData(getOrderTrendData());
      setDelivery(getPickupStatusBreakdown());
      setPeak(getPeakHoursData());
      setCustomer(getCustomerGrowthData());
    });
  }, []);

  const totalRevenue = revenueData.reduce((s, d) => s + d.revenue, 0);
  const totalOrders = revenueData.reduce((s, d) => s + d.orders, 0);
  const totalCommission = revenueData.reduce((s, d) => s + d.commission, 0);

  const tabs = [
    { key: 'revenue', label: 'Revenue' },
    { key: 'orders', label: 'Orders' },
    { key: 'pickup', label: 'Pickup' },
    { key: 'customers', label: 'Customers' },
  ];

  return (
    <div>
      <div className="admin-page-header">
        <div>
          <h1 className="admin-page-title">Statistics & Reports</h1>
          <p className="admin-page-subtitle">Comprehensive platform analytics and insights</p>
        </div>
        <Button variant="secondary" icon={<Download size={14} />}>Export Report</Button>
      </div>

      {/* KPI Summary */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: 16, marginBottom: 24 }}>
        {[
          { label: 'Annual Revenue', value: formatCurrency(totalRevenue), color: '#E85D04', icon: '💰' },
          { label: 'Annual Orders', value: totalOrders.toLocaleString(), color: '#3B82F6', icon: '📦' },
          { label: 'Annual Commission', value: formatCurrency(totalCommission), color: '#10B981', icon: '💎' },
          { label: 'Avg Monthly Revenue', value: formatCurrency(totalRevenue / 12), color: '#8B5CF6', icon: '📊' },
          { label: 'Commission Rate', value: '12%', color: '#F59E0B', icon: '🔖' },
          { label: 'Platform Growth', value: '+18.4%', color: '#10B981', icon: '📈' },
        ].map((item, i) => (
          <div key={i} style={{ background: 'var(--admin-surface)', border: '1px solid var(--admin-border)', borderRadius: 12, padding: '16px', borderLeft: `4px solid ${item.color}` }}>
            <div style={{ fontSize: 22 }}>{item.icon}</div>
            <div style={{ fontSize: 20, fontWeight: 800, color: item.color, marginTop: 6 }}>{item.value}</div>
            <div style={{ fontSize: 12, color: 'var(--admin-text-secondary)' }}>{item.label}</div>
          </div>
        ))}
      </div>

      <div className="admin-card">
        <div className="admin-card-header">
          <Tabs tabs={tabs} active={tab} onChange={setTab} />
        </div>
        <div className="admin-card-body">
          {tab === 'revenue' && (
            <div>
              <h3 className="admin-chart-title">Monthly Revenue vs Commission (2026)</h3>
              <ResponsiveContainer width="100%" height={320}>
                <BarChart data={revenueData}>
                  <CartesianGrid strokeDasharray="3 3" stroke="var(--admin-border)" />
                  <XAxis dataKey="month" tick={{ fontSize: 12, fill: 'var(--admin-text-secondary)' }} />
                  <YAxis tick={{ fontSize: 12, fill: 'var(--admin-text-secondary)' }} tickFormatter={v => `$${v >= 1000000 ? (v/1000000).toFixed(1)+"M" : v >= 1000 ? (v/1000).toFixed(0)+"K" : v}`} />
                  <Tooltip formatter={(v, n) => [`$${v >= 1000000 ? (v/1000000).toFixed(2)+"M" : v >= 1000 ? (v/1000).toFixed(1)+"K" : v}`, n]} contentStyle={{ background: 'var(--admin-surface)', border: '1px solid var(--admin-border)', borderRadius: 8 }} />
                  <Legend />
                  <Bar dataKey="revenue" fill="#E85D04" radius={[4, 4, 0, 0]} name="Revenue" />
                  <Bar dataKey="commission" fill="#10B981" radius={[4, 4, 0, 0]} name="Commission" />
                </BarChart>
              </ResponsiveContainer>
            </div>
          )}
          {tab === 'orders' && (
            <div>
              <h3 className="admin-chart-title">Weekly Order Performance</h3>
              <ResponsiveContainer width="100%" height={320}>
                <LineChart data={orderData}>
                  <CartesianGrid strokeDasharray="3 3" stroke="var(--admin-border)" />
                  <XAxis dataKey="day" tick={{ fontSize: 12, fill: 'var(--admin-text-secondary)' }} />
                  <YAxis tick={{ fontSize: 12, fill: 'var(--admin-text-secondary)' }} />
                  <Tooltip contentStyle={{ background: 'var(--admin-surface)', border: '1px solid var(--admin-border)', borderRadius: 8 }} />
                  <Legend />
                  <Line type="monotone" dataKey="orders" stroke="#3B82F6" strokeWidth={3} name="Total Orders" />
                  <Line type="monotone" dataKey="pickedUp" stroke="#10B981" strokeWidth={3} name="Picked Up" />
                  <Line type="monotone" dataKey="cancelled" stroke="#EF4444" strokeWidth={2} strokeDasharray="5 5" name="Cancelled" />
                </LineChart>
              </ResponsiveContainer>
            </div>
          )}
          {tab === 'pickup' && (
            <div>
              <h3 className="admin-chart-title" style={{ textAlign: 'center' }}>Pickup Status Breakdown</h3>
              <div style={{ display: 'flex', gap: 24 }}>
                <ResponsiveContainer width="50%" height={320}>
                  <PieChart>
                    <Pie data={deliveryData} cx="50%" cy="50%" outerRadius={120} dataKey="value" label={({ name, value }) => `${name}: ${value}`}>
                      {deliveryData.map((entry, index) => <Cell key={index} fill={entry.color || COLORS[index % COLORS.length]} />)}
                    </Pie>
                    <Tooltip />
                    <Legend />
                  </PieChart>
                </ResponsiveContainer>
                <ResponsiveContainer width="50%" height={320}>
                  <BarChart data={peakData}>
                    <CartesianGrid strokeDasharray="3 3" stroke="var(--admin-border)" />
                    <XAxis dataKey="hour" tick={{ fontSize: 11 }} />
                    <YAxis tick={{ fontSize: 11 }} />
                    <Tooltip contentStyle={{ background: 'var(--admin-surface)', border: '1px solid var(--admin-border)', borderRadius: 8 }} />
                    <Bar dataKey="orders" fill="#E85D04" radius={[4,4,0,0]} name="Orders" maxBarSize={30} />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </div>
          )}
          {tab === 'customers' && (
            <div>
              <h3 className="admin-chart-title">Customer Growth (2026)</h3>
              <ResponsiveContainer width="100%" height={320}>
                <BarChart data={customerData}>
                  <CartesianGrid strokeDasharray="3 3" stroke="var(--admin-border)" />
                  <XAxis dataKey="month" tick={{ fontSize: 12, fill: 'var(--admin-text-secondary)' }} />
                  <YAxis tick={{ fontSize: 12, fill: 'var(--admin-text-secondary)' }} />
                  <Tooltip contentStyle={{ background: 'var(--admin-surface)', border: '1px solid var(--admin-border)', borderRadius: 8 }} />
                  <Legend />
                  <Bar dataKey="newCustomers" fill="#E85D04" radius={[4, 4, 0, 0]} name="New Customers" />
                  <Bar dataKey="returning" fill="#3B82F6" radius={[4, 4, 0, 0]} name="Returning Customers" />
                </BarChart>
              </ResponsiveContainer>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default Statistics;
